// Pairing on the phone (SPEC 7.1 steps 5 to 7; protocol steps 3 and 5 to 7). Each failure is a plain
// stop screen. The SAS is shown large and calm; the desk types it, the phone never sends it.
import { fingerprint } from "@whistle/protocol";
import { useEffect, useRef, useState } from "react";
import { useApp, useAppState } from "../app/AppContext";
import { navigate, rewriteUrl } from "../app/router";
import { PRODUCT_NAME } from "../brand";
import type { PairingRecord } from "../lib/model";
import { PairError, defaultDeviceName, groupFingerprint, guessDeviceName, isOwnPairing, relayHost, samePairing, sameRelay, type PairTarget } from "../lib/pairing";
import { enablePushFailureText } from "../lib/push";
import { Banner } from "../components/Banner";
import { IconArrow } from "../components/Icons";
import { Brand, Page } from "../components/Shell";

export type PairRunner = {
  /** Checks the link against the relay and says what it points at. Nothing is stored or replaced yet. */
  check(): Promise<PairTarget>;
  join(deviceName: string): Promise<{ sas: string }>;
  wait(signal: AbortSignal): Promise<PairingRecord>;
};

type Step =
  | { s: "checking" }
  | { s: "confirm"; target: PairTarget; current: PairingRecord }
  | { s: "name"; target: PairTarget; replacing: PairingRecord | null }
  | { s: "joining"; target: PairTarget; replacing: PairingRecord | null }
  | { s: "sas"; machineName: string; sas: string }
  | { s: "done"; record: PairingRecord }
  | { s: "already"; record: PairingRecord }
  /** `again`: the machine this phone is already paired with, when the one-phone limit is this phone's own old pairing. */
  | { s: "stop"; code: PairError["code"] | "unsupported"; text: string; again?: string };

export function Pair({ hash }: { hash: string }) {
  const { controller, pairRunner } = useApp();
  const s = useAppState();
  const [step, setStep] = useState<Step>({ s: "checking" });
  const [name, setName] = useState(() => defaultDeviceName());
  const nameEdited = useRef(false);
  const [alerts, setAlerts] = useState<"idle" | "busy" | "on" | "off">("idle");
  const [alertNote, setAlertNote] = useState<string | null>(null);
  const runner = useRef<PairRunner | null>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    let alive = true;
    void guessDeviceName().then((n) => alive && !nameEdited.current && setName(n));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    // A pairing this phone has been removed from does not count: the owner must be able to pair again.
    const paired = () => {
      const st = controller.store.getState();
      return st.removed ? null : st.pairing;
    };
    // After pairing the secret is taken out of the URL, so a reload lands here with no code at all.
    const own = paired();
    if (own && !hash.replace(/^#/, "")) {
      setStep({ s: "already", record: own });
      return;
    }
    const r = pairRunner(hash);
    runner.current = r;
    r.check()
      .then((target) => {
        if (!alive) return;
        // Security review 2, S1: a link for another machine, account or relay never replaces a pairing on one tap.
        const current = paired();
        setStep(current && !samePairing(current, target) ? { s: "confirm", target, current } : { s: "name", target, replacing: null });
      })
      .catch((e) => {
        if (!alive) return;
        const record = paired();
        setStep(record && isOwnPairing(hash, record, e) ? { s: "already", record } : stopFor(e));
      });
    return () => {
      alive = false;
      abort.current?.abort();
    };
  }, [hash, pairRunner, controller]);

  // The fingerprint of the machine this phone is paired with now, beside the link's, on the confirmation.
  const [currentPrint, setCurrentPrint] = useState<string | null>(null);
  const confirming = step.s === "confirm" ? step.current : null;
  useEffect(() => {
    let alive = true;
    setCurrentPrint(null);
    if (confirming)
      fingerprint(confirming.machine).then(
        (f) => alive && setCurrentPrint(f),
        () => {},
      );
    return () => {
      alive = false;
    };
  }, [confirming]);

  const connect = async () => {
    if (step.s !== "name" || !runner.current) return;
    const { target, replacing } = step;
    const machineName = target.machineName;
    const deviceName = name.trim() || "Phone";
    setStep({ s: "joining", target, replacing });
    try {
      const { sas } = await runner.current.join(deviceName);
      setStep({ s: "sas", machineName, sas });
      abort.current = new AbortController();
      const record = await runner.current.wait(abort.current.signal);
      await controller.completePairing(record);
      // The code is spent: drop it from the address bar so a reload shows "already paired", not "expired".
      rewriteUrl("/pair");
      setStep({ s: "done", record });
    } catch (e) {
      // Every pairing joins with a new key, so pairing this phone again with its own machine meets the one-phone limit
      // until the old pairing is removed at the desk.
      const st = controller.store.getState();
      const own = !st.removed && st.pairing && samePairing(st.pairing, target);
      const stop = stopFor(e);
      setStep(stop.s === "stop" && stop.code === "device-limit" && own ? { ...stop, again: target.machineName } : stop);
    }
  };

  const turnOnAlerts = async () => {
    setAlerts("busy");
    const r = await controller.enablePush();
    if (r.ok) {
      setAlerts("on");
      setAlertNote("Alerts are on. To test from the desk, set Alerts to Away in Connections first (Automatic stays quiet while you are at the keyboard), then lock the phone and send a test question.");
    } else {
      setAlerts("off");
      const text = enablePushFailureText(r.why, r.health.lastError);
      setAlertNote(r.why === "not-connected" ? text : `${text} The inbox works without alerts while the app is open.`);
    }
  };

  const openInbox = () => navigate("/", { replace: true });
  const alertsNote = alertNote && (
    <p className="help" role="status">
      {alertNote}
    </p>
  );

  return (
    <>
      <header className="topbar">
        <Brand />
      </header>
      <Page tabs={false} className="pair">
        {step.s === "checking" && (
          <>
            <h2>Checking the code.</h2>
            <p className="waiting">
              <span className="pulse" aria-hidden="true" /> Asking the relay which PC made it.
            </p>
          </>
        )}

        {step.s === "stop" && (
          <>
            <div className="stop" role="alert">
              <b>
                {step.code === "mismatch"
                  ? "This code doesn't match your computer."
                  : step.code === "expired"
                    ? "This code has expired."
                    : step.code === "relay"
                      ? "This link uses a relay this app doesn't connect to."
                      : step.code === "device-limit"
                      ? step.again
                        ? `This phone is already paired with ${step.again}.`
                        : "Another phone is already paired."
                      : "Can't pair with this code."}
              </b>
              <p>{withCode(step.text)}</p>
            </div>
            <p>
              {step.code === "mismatch" ? (
                "The relay's description of the PC differs from what the QR code says. Don't type anything at the desk. Press Ctrl+C there, run whistle pair again, and scan the new code. If it happens twice, something between your PC and this phone is wrong; stop and check your network."
              ) : step.code === "relay" ? (
                <>
                  Nothing was changed. A code from <code>whistle pair</code> on your own PC names the relay this app uses, so this link came from somewhere else. Don't use it.
                </>
              ) : step.code === "unsupported" ? (
                "Open the link in Chrome or Samsung Internet, outside a private tab, and scan the code again."
              ) : step.code === "device-limit" && step.again ? (
                <>
                  Each pairing gives this phone a new key, and {PRODUCT_NAME} v0.1 pairs one phone with one PC, so remove the old pairing first. At your desk, <code>whistle devices</code> shows it and <code>whistle revoke NAME</code> removes it. Then run <code>whistle pair</code> and scan the new code.
                </>
              ) : step.code === "device-limit" ? (
                <>
                  {PRODUCT_NAME} v0.1 pairs one phone with one PC. At your desk, <code>whistle devices</code> shows the paired phone and <code>whistle revoke NAME</code> removes it. Then run <code>whistle pair</code> and scan the new code.
                </>
              ) : (
                "Press Enter at your desk for a fresh code and scan it again."
              )}
            </p>
          </>
        )}

        {step.s === "confirm" && (
          <ConfirmReplace target={step.target} current={step.current} currentPrint={currentPrint} onKeep={openInbox} onReplace={() => setStep({ s: "name", target: step.target, replacing: step.current })} />
        )}

        {(step.s === "name" || step.s === "joining") && (
          <>
            <h2>Connect this phone to {step.target.machineName}.</h2>
            <div className="machine">
              <span className="brandmark" aria-hidden="true">
                <i />
                <i />
              </span>
              <div>
                <b>{step.target.machineName}</b>
                <span>Identity checked against the code.</span>
                <span>Relay {relayHost(step.target.relayOrigin)}</span>
              </div>
            </div>
            {step.replacing && (
              <Banner tone="warn" icon="alert" title={`This replaces the pairing with ${step.replacing.machine.name}.`}>
                {PRODUCT_NAME} v0.1 pairs one phone with one PC. Once you connect, {step.replacing.machine.name}'s questions stop reaching this phone.
              </Banner>
            )}
            <div className="field">
              <label htmlFor="device-name">This phone's name</label>
              <input
                id="device-name"
                value={name}
                onChange={(e) => {
                  nameEdited.current = true;
                  setName(e.target.value);
                }}
                maxLength={40}
                autoComplete="off"
                disabled={step.s === "joining"}
              />
              <span className="help">Shown at the desk in <code>whistle devices</code>.</span>
            </div>
            <button type="button" className="btn btn-primary btn-block" disabled={step.s === "joining" || !name.trim()} onClick={connect}>
              {step.s === "joining" ? "Connecting…" : `Connect this phone to ${step.target.machineName}`}
              <IconArrow />
            </button>
            <p className="small muted">Your PC trusts this phone only after you type the code it shows next.</p>
          </>
        )}

        {step.s === "sas" && (
          <>
            <h2>Type this code on your computer.</h2>
            <div className="sas" aria-live="polite">
              <div className="code" aria-label={`Code ${step.sas.replace("-", " ").split("").join(" ")}`}>
                {step.sas.slice(0, 3)}
                <em aria-hidden="true">-</em>
                {step.sas.slice(4)}
              </div>
              <span className="waiting">
                <span className="pulse" aria-hidden="true" /> Waiting for {step.machineName}
              </span>
            </div>
            <p>
              The terminal is asking for it. Upper or lower case both work. If it doesn't match on the third try, pairing is cancelled and you scan again.
            </p>
          </>
        )}

        {step.s === "done" && (
          <>
            <h2>Paired with {step.record.machine.name}.</h2>
            <p>Questions from its agents now reach this phone, end to end encrypted. One thing left: alerts, so you hear about them with the phone locked.</p>
            {alerts !== "on" && (
              <button type="button" className="btn btn-primary btn-block" disabled={alerts === "busy"} onClick={turnOnAlerts}>
                {alerts === "busy" ? "Turning on…" : "Turn on alerts"}
              </button>
            )}
            <p className="small muted">An alert says how many agents need an answer. Never the question, a command, or anything from your terminal.</p>
            {alertsNote}
            <button type="button" className={`btn ${alerts === "on" || alerts === "off" ? "btn-primary" : "btn-ghost"} btn-block`} onClick={openInbox}>
              {alerts === "on" || alerts === "off" ? "Open the inbox" : "Not now"}
            </button>
          </>
        )}

        {step.s === "already" && (
          <>
            <h2>Already paired with {step.record.machine.name}.</h2>
            <p>This phone is connected to {step.record.machine.name}. Its code has been used, so there's nothing to type at the desk.</p>
            {s.push.supported && !s.push.subscribed && alerts !== "on" && (
              <button type="button" className="btn btn-ghost btn-block" disabled={alerts === "busy"} onClick={turnOnAlerts}>
                {alerts === "busy" ? "Turning on…" : "Turn on alerts"}
              </button>
            )}
            {alertsNote}
            <button type="button" className="btn btn-primary btn-block" onClick={openInbox}>
              Open the inbox
            </button>
          </>
        )}
      </Page>
    </>
  );
}

/**
 * Security review 2, S1: anyone can make a pairing link, name it after this phone's PC and point it at their own relay.
 * Before one replaces the pairing, the phone says what changes by key, account and relay, never by name, and asks
 * twice: here, then Connect.
 */
function ConfirmReplace({ target, current, currentPrint, onKeep, onReplace }: { target: PairTarget; current: PairingRecord; currentPrint: string | null; onKeep: () => void; onReplace: () => void }) {
  const otherRelay = !sameRelay(target.relayOrigin, current.relayOrigin);
  const what =
    target.machineId !== current.machineId
      ? target.machineName === current.machine.name
        ? `This link is for a different computer, also called ${target.machineName}.`
        : `This link is for a different computer: ${target.machineName}.`
      : target.accountId !== current.accountId
        ? `This link is for ${target.machineName} on a different account.`
        : `This link connects ${target.machineName} through a different relay.`;
  return (
    <>
      <h2>This phone is paired with {current.machine.name}.</h2>
      <div className="stop" role="alert">
        <b>{what}</b>
        <p>Connecting replaces that pairing. Questions from {current.machine.name} stop reaching this phone, and the computer in this link can send it questions instead.</p>
      </div>
      {otherRelay && (
        <Banner tone="danger" icon="alert" title={`Different relay: ${relayHost(target.relayOrigin)}`}>
          This phone's pairing goes through {relayHost(current.relayOrigin)}. Only continue if you set up {relayHost(target.relayOrigin)} yourself.
        </Banner>
      )}
      <MachineCard label="Paired now" name={current.machine.name} relayOrigin={current.relayOrigin} print={currentPrint} />
      <MachineCard label="This link" name={target.machineName} relayOrigin={target.relayOrigin} print={target.fingerprint} />
      <p>
        Only continue if you ran <code>whistle pair</code> on that computer yourself, just now. Never for a link someone sent you.
      </p>
      <button type="button" className="btn btn-primary btn-block" onClick={onKeep}>
        Keep {current.machine.name}
      </button>
      <button type="button" className="btn btn-danger btn-block" onClick={onReplace}>
        Replace with {target.machineName}
      </button>
    </>
  );
}

function MachineCard({ label, name, relayOrigin, print }: { label: string; name: string; relayOrigin: string; print: string | null }) {
  return (
    <div className="machine" role="group" aria-label={label}>
      <span className="brandmark" aria-hidden="true">
        <i />
        <i />
      </span>
      <div>
        <span>{label}</span>
        <b>{name}</b>
        <span>Relay {relayHost(relayOrigin)}</span>
        <span className="mono">Fingerprint {print ? groupFingerprint(print) : "…"}</span>
      </div>
    </div>
  );
}

/** Our own messages mark commands with backticks; show those as code. */
function withCode(text: string) {
  return text.split("`").map((part, i) => (i % 2 ? <code key={i}>{part}</code> : part));
}

function stopFor(e: unknown): Step {
  if (e instanceof PairError) return { s: "stop", code: e.code, text: e.message };
  if (e instanceof Error && /unsupported|keys|storage/i.test(e.message)) return { s: "stop", code: "unsupported", text: e.message };
  return { s: "stop", code: "bad-response", text: e instanceof Error ? e.message : "Something went wrong." };
}
